<template>
	<div class="row mb-3">
		<div class="col-12 col-sm-4">
			<div class="d-flex body-strong text-grey-900 text-start mb-2">
				<span v-if="currentPackConfig.length > 0">
					{{ content.planningSummary_options_optionTitle }}
				</span>
				<span v-else>
					{{ content.planner_resumeNoOptions }}
				</span>
			</div>
			<div class="body text-start text-grey-700 mb-2 mb-md-0">
				<span v-if="currentPackConfig.length > 0">
					<template v-if="proposedOriginByWiyp">
						{{ content.planningSummary_options_optionSubtitle_flight}}
					</template>
					<template v-else>
						{{ content.planningSummary_options_optionSubtitle }}
					</template>
				</span>
				<span v-else>
					{{ content.planner_resumeAddOption }}
				</span>
			</div>
		</div>
		<!-- hide for now, but will need later when the design and planned behavior are finalized -->
		<!-- <div class="d-none d-sm-flex flex-sm-column col-8"> -->
		<div
			class="d-flex flex-column col-12 col-sm-8 body text-grey-900 text-start"
		>
			<span
				class="my-1 my-md-0 d-flex flex-row flex-nowrap align-items-center"
				v-for="(optionName, option_i) in currentPackConfig"
				:key="option_i"
			>
				<div class="me-1">
					<svg :height="16" :width="16" class="icon">
						<use :xlink:href="`#___icon___${optionName}`"></use>
					</svg>
				</div>
				<span class="me-1">{{ content[`planningSummary_options_${optionName}`] }}
				</span>
				<span>
					<ParticipantsTotals
						:participants="participants"
						:content="content"
						:optionName="optionName"
						:variant="variant"
						:displayWithIcons="true"
						:excludedAges="[1]"
					/>
				</span>
				<span class="ms-1" v-if="optionName==='flight' && proposedOriginByWiyp">
					{{ originAirport  }}					
				</span>
			</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, watch, ref } from "vue";
import ParticipantsTotals from "@/vue/planning/components/common/participantsTotals.vue";
import packOptionConfig from "@/config/packOptionsConfig.js";
import { useStore } from "@/vue/planning/store/store";
import type { Variant } from "@/types/index";
import { isOriginHub } from "@/vue/planning/logic/isOriginHub";
import { airportCodeMap } from "@/config/airportCodeMap";
import flightPackCode from "@/config/flightPackCode";
import transportPackCode from "@/config/transportPackCode";
import { trainStationCodeMap } from "@/config/trainStationCodeMap";

// Props
interface Props {
	variant?: Variant;
}

const props = withDefaults(defineProps<Props>(), {
	variant: () => ({} as Variant),
});

// Store
const store = useStore();

// Ref
const proposedOriginByWiyp = ref<string | null>(null);

// Computed
const packCode = computed<string>(() => store.state.packCode);
const currentPackCode = computed<string>(() => store.state.currentPackCode);
const content = computed<Record<string, string>>(() => store.state.content);
const participants = computed<number[]>(() => store.state.participants);
const locale = computed<string>(() => store.getters.locale);
const flightOrigin = computed<string | null>(() => store.state.flightOrigin);

const currentPackConfig = computed<string[]>(() => {
	const config = packOptionConfig as Record<string, string[]>;
	if (props.variant?.codePackage && config[props.variant.codePackage]) {
		return config[props.variant.codePackage];
	}
	return config[packCode.value] || [];
});

const originAirport = computed<string | null>(() => {
	// Recompute when flightOrigin or proposedOriginByWiyp changes
	const originToUse = proposedOriginByWiyp.value || flightOrigin.value;
	const airportName = airportCodeMap.get(originToUse || "");
	if (airportName && airportName.name) {
		return `${content.value['planningSummary_options_from']} ${airportName.name}`;
	}
	return null;
});
// Watch for when variant and store state become available
watch(
	() => [props.variant, currentPackCode.value, flightOrigin.value] as const,
	([variant, currentPackCodeValue, flightOriginValue]) => {
		// Only proceed if we have the necessary data
		if (!variant?.codePackage || !currentPackCodeValue) {
			return;
		}

		proposedOriginByWiyp.value = null;
		const isFlightPackage = flightPackCode.includes(currentPackCodeValue);		
		if (isFlightPackage && flightOriginValue) {
			const wiypCall = store.getters.getWiypCallByFamilyCompositionAndPackageCodeAndId(
				`${store.state.participants.join('-')}-${store.state.flightOrigin}`,
				variant.codePackage,
				variant.prestId
			);
			const isSelectedOriginHubValue = isOriginHub(flightOriginValue, airportCodeMap) || isOriginHub(flightOriginValue, trainStationCodeMap);
			if (isSelectedOriginHubValue && wiypCall?.flightOrigin) {
				proposedOriginByWiyp.value = wiypCall.flightOrigin;
			}			
		} else {
			proposedOriginByWiyp.value = null;
		}
	},
	{ immediate: true } // Run immediately to check initial values
);
</script>

<style scoped></style>
